import React from "react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/apiClient";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";

/**
 * Shared datalist for HVAC unit codes — rendered ONCE by parent (via id
 * `purchase-upload-unit-options`) and referenced by every Unit input in the
 * lines table.
 */
export const PURCHASE_UNIT_DATALIST_ID = "purchase-upload-unit-options";
export const PURCHASE_UNIT_OPTIONS = [
  "nos","pcs","pack","carton","sheet","coil","set","pair","roll","bundle","drum","bag","box","lot",
  "kg","g","ton","meter","mm","cm","ft","inch","sqm","sqft","cum","cuft","litre","ml",
  "hour","day","week","month",
];

export function PurchaseUnitDatalist() {
  return (
    <datalist id={PURCHASE_UNIT_DATALIST_ID}>
      {PURCHASE_UNIT_OPTIONS.map((v) => <option key={v} value={v} />)}
    </datalist>
  );
}

/**
 * PurchaseLinesTable — extracted from PurchaseUploadSheet.jsx (iter49 refactor).
 * Editable line-items table with HSN / qty / unit / rate / GST% columns +
 * material autocomplete on the description column.
 *
 * Props
 *   • items[]                          — the line-items array
 *   • onUpdateItem(idx, key, value)    — per-cell edit
 *   • onPickMaterial(idx, material)    — apply a MaterialAutocomplete pick
 *   • onAddItem()                      — add blank row
 *   • onRemoveItem(idx)                — delete row
 */
export default function PurchaseLinesTable({ items, onUpdateItem, onPickMaterial, onAddItem, onRemoveItem }) {
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
          Line Items · {items.length}
        </div>
        <button onClick={onAddItem} data-testid="purchase-add-item-btn" className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1">
          <Plus size={12} /> Add row
        </button>
      </div>
      <Table className="bc-list text-[12.5px]">
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead className="w-24">HSN</TableHead>
            <TableHead className="w-20 text-right">Qty</TableHead>
            <TableHead className="w-20">Unit</TableHead>
            <TableHead className="w-28 text-right">Rate</TableHead>
            <TableHead className="w-20 text-right">GST%</TableHead>
            <TableHead className="w-28 text-right">Line ₹</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it, i) => {
            const line = Number(it.qty || 0) * Number(it.unit_price || 0);
            return (
              <TableRow key={`purchase-line-${i}`} data-testid={`purchase-line-${i}`}>
                <TableCell>
                  <MaterialAutocomplete
                    value={it.description}
                    onChange={(v) => onUpdateItem(i, "description", v)}
                    onPick={(m) => onPickMaterial(i, m)}
                    placeholder="Search past materials…"
                    testid={`purchase-line-desc-${i}`}
                  />
                </TableCell>
                <TableCell><Input value={it.hsn} onChange={(e) => onUpdateItem(i, "hsn", e.target.value)} className="h-7 font-mono text-[11.5px]" data-testid={`purchase-line-hsn-${i}`} /></TableCell>
                <TableCell><Input type="number" value={it.qty} onChange={(e) => onUpdateItem(i, "qty", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" data-testid={`purchase-line-qty-${i}`} /></TableCell>
                <TableCell><Input list={PURCHASE_UNIT_DATALIST_ID} value={it.unit || "nos"} onChange={(e) => onUpdateItem(i, "unit", e.target.value)} className="h-7 text-[12px] uppercase tracking-wider" placeholder="nos" data-testid={`purchase-line-unit-${i}`} /></TableCell>
                <TableCell><Input type="number" value={it.unit_price} onChange={(e) => onUpdateItem(i, "unit_price", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" data-testid={`purchase-line-rate-${i}`} /></TableCell>
                <TableCell><Input type="number" value={it.gst_rate} onChange={(e) => onUpdateItem(i, "gst_rate", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" data-testid={`purchase-line-gst-${i}`} /></TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(line)}</TableCell>
                <TableCell><button onClick={() => onRemoveItem(i)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button></TableCell>
              </TableRow>
            );
          })}
          {items.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center py-4 text-slate-500 text-[12px]">No items — click Add row</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
